import React from "react";
import { useForm, useFieldArray } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { format, eachDayOfInterval, parseISO } from "date-fns";
import { useAppDispatch, useAppSelector } from "@/app/hooks";
import { useNotification } from "@/context/NotificationContext";
import {
  setSessionDayTime,
  setSessionDayTimes,
  selectSessionDays,
  clearSessionDays,
} from "@/modules/eventRegistration/features/EventSessionManagement/slices/eventSessionDaySlice";
import {
  eventSessionFormSchema,
  type EventSessionFormValues,
} from "@/modules/eventRegistration/features/EventSessionManagement/hooks/eventSessionDayFormSchema";
import { fetchEventSessionMetadata } from "@/modules/eventRegistration/features/EventSessionManagement/api/fetchEventSessionMetadata/fetchEventSessionMetadataApi";
import {
  createEventSessionAction,
  updateEventSessionAction,
} from "@/modules/eventRegistration/features/EventSessionManagement/slices/createUpdateEventSessionSlice";
import {
  generateTimeOptions,
  convertUtcToTimezone,
  convertTimezoneToUtc,
} from "@/utils/timeUtils";

interface UseEventSessionFormProps {
  isEditMode?: boolean;
  eventUuid: string;
  sessionUuid?: string | null;
  onSuccessCallback?: () => void;
}

const SESSION_DAY_DEFAULT_TIME = { start: "08:00", end: "16:00" } as const;

export function useEventSessionForm({
  isEditMode = false,
  eventUuid,
  sessionUuid,
  onSuccessCallback,
}: UseEventSessionFormProps) {
  const dispatch = useAppDispatch();
  const { showNotification } = useNotification();

  const { singleSession, loadingCreate, loadingUpdate } = useAppSelector(
    (state) => state.createUpdateEventSession,
  );

  const isLoading = isEditMode ? loadingUpdate : loadingCreate;

  const formId = React.useMemo(() => {
    if (isEditMode && sessionUuid) return `edit-session-${sessionUuid}`;
    if (eventUuid) return `create-session-${eventUuid}`;
    return "default";
  }, [isEditMode, sessionUuid, eventUuid]);

  const [timezones, setTimezones] = React.useState<
    Array<{ label: string; value: string; id: number }>
  >([]);
  const [isTimezonesLoading, setIsTimezonesLoading] = React.useState(false);
  const isInitialMount = React.useRef(true);
  const hasProcessedInitialData = React.useRef(false);
  // Tracks the start/end range the day list was last generated for, so the
  // regeneration effect only rebuilds the list when the range *actually*
  // changes — not on identity-only churn (e.g. editing a day's time updates
  // redux, which recreates the effect's callback deps). Without this guard a
  // time edit would resurrect dates the admin removed.
  const lastGeneratedRangeRef = React.useRef<{
    start?: string;
    end?: string;
  } | null>(null);

  const getCreateSessionDefaultFormValues = () => ({
    name: "",
    startDate: "",
    endDate: "",
    maxEnrollments: 0,
    virtualLink: "",
    notes: "",
    isPublished: false,
    isVirtualOnly: false,
    venueId: null,
    instructorId: null,
    timezoneId: undefined as unknown as number,
    sessionDays: undefined,
  });

  const getEditSessionFormValues = (
    session: NonNullable<typeof singleSession>,
  ) => ({
    name: session.name || "",
    startDate: session.startDate || "",
    endDate: session.endDate || "",
    maxEnrollments: session.maxEnrollments ?? 0,
    virtualLink: session.virtualLink || "",
    notes: session.notes || "",
    isPublished: session.isPublished ?? false,
    isVirtualOnly: session.isVirtualOnly ?? false,
    venueId:
      session.venueId != null
        ? {
            id: session.venueId,
            name: session.venueName ?? "Unknown venue",
          }
        : null,
    timezoneId: session.timezoneId ?? (undefined as unknown as number),
    instructorId:
      session.instructorId !== null
        ? {
            id: session.instructorId,
            name: session.instructorName ?? "Unknown Instructor",
          }
        : null,
    sessionDays: session.sessionDays?.map((sessionDay) => ({
      id: sessionDay.id,
      startDate: sessionDay.startDate,
      endDate: sessionDay.endDate,
    })),
  });

  const initialData = React.useMemo(() => {
    if (isEditMode && singleSession) {
      return getEditSessionFormValues(singleSession);
    }
    return getCreateSessionDefaultFormValues();
  }, [isEditMode, singleSession]);

  const storedSessionDays = useAppSelector((state) =>
    selectSessionDays(state, formId),
  );

  const memoizedStoredSessionDays = React.useMemo(
    () => storedSessionDays,
    [JSON.stringify(storedSessionDays)],
  );

  const formMethods = useForm<EventSessionFormValues>({
    resolver: zodResolver(eventSessionFormSchema),
    defaultValues: initialData,
  });

  const {
    handleSubmit,
    control,
    formState: { isSubmitting },
    watch,
    setValue,
  } = formMethods;

  const { fields, replace, remove } = useFieldArray({
    control,
    name: "sessionDays",
  });

  const formValues = {
    maxEnrollments: watch("maxEnrollments") ?? 0,
    isVirtualOnly: watch("isVirtualOnly") ?? false,
    timezoneId: watch("timezoneId"),
    startDate: watch("startDate"),
    endDate: watch("endDate"),
  };

  const selectedTimezone = React.useMemo(() => {
    if (!formValues.timezoneId) return undefined;
    const timezone = timezones.find((t) => t.id === formValues.timezoneId);
    return timezone?.value ? timezone : undefined;
  }, [formValues.timezoneId, timezones]);

  const timeOptions = React.useMemo(() => generateTimeOptions(), []);

  const getSessionDayTimesFromStoredData = React.useCallback(
    (dateKey: string) => {
      const storedSessionDay = memoizedStoredSessionDays[dateKey];
      if (storedSessionDay) {
        return {
          date: dateKey,
          startTime: storedSessionDay.startTime,
          endTime: storedSessionDay.endTime,
        };
      }
      return null;
    },
    [memoizedStoredSessionDays],
  );

  const getSessionDayTimesFromExistingDates = (
    sessionDay: { startDate?: string; endDate?: string },
    dateKey: string,
  ) => {
    if (
      initialData.sessionDays !== undefined &&
      sessionDay.startDate &&
      sessionDay.endDate
    ) {
      try {
        const startDate = new Date(sessionDay.startDate);
        const endDate = new Date(sessionDay.endDate);

        const startHours = startDate.getHours().toString().padStart(2, "0");
        const startMinutes = startDate.getMinutes().toString().padStart(2, "0");
        const endHours = endDate.getHours().toString().padStart(2, "0");
        const endMinutes = endDate.getMinutes().toString().padStart(2, "0");

        return {
          date: dateKey,
          startTime: `${startHours}:${startMinutes}`,
          endTime: `${endHours}:${endMinutes}`,
        };
      } catch (error) {
        console.error("Error parsing dates:", error);
      }
    }
    return null;
  };

  const getTimesFromTimezone = (sessionDay: {
    startDate?: string;
    endDate?: string;
  }) => {
    if (!sessionDay.startDate || !sessionDay.endDate) {
      return {
        date: "",
        startTime: SESSION_DAY_DEFAULT_TIME.start,
        endTime: SESSION_DAY_DEFAULT_TIME.end,
      };
    }

    const startConverted = convertUtcToTimezone(
      sessionDay.startDate,
      selectedTimezone!.value,
    );
    const endConverted = convertUtcToTimezone(
      sessionDay.endDate,
      selectedTimezone!.value,
    );
    return {
      date: startConverted.date,
      startTime: startConverted.time,
      endTime: endConverted.time,
    };
  };

  const getDisplayPropertiesForSessionDay = React.useCallback(
    (sessionDay: {
      startDate?: string;
      endDate?: string;
      id?: string | number;
    }) => {
      const dateKey = sessionDay.startDate
        ? format(parseISO(sessionDay.startDate), "yyyy-MM-dd")
        : "";

      if (!selectedTimezone?.value) {
        const currentStoredData = getSessionDayTimesFromStoredData(dateKey);
        return (
          currentStoredData ||
          getSessionDayTimesFromExistingDates(sessionDay, dateKey) || {
            date: dateKey,
            startTime: SESSION_DAY_DEFAULT_TIME.start,
            endTime: SESSION_DAY_DEFAULT_TIME.end,
          }
        );
      }

      return getTimesFromTimezone(sessionDay);
    },
    [selectedTimezone?.value, getSessionDayTimesFromStoredData],
  );

  const createSessionDayEntry = React.useCallback(
    (day: Date) => {
      const dateKey = format(day, "yyyy-MM-dd");
      const storedSessionDay = memoizedStoredSessionDays[dateKey];

      let startTime: string;
      let endTime: string;
      let existingId: number | undefined;

      if (initialData.sessionDays) {
        const existingSessionDay = initialData.sessionDays.find(
          (sessionDay) => {
            const sessionDayDate = sessionDay.startDate
              ? format(parseISO(sessionDay.startDate), "yyyy-MM-dd")
              : "";
            return sessionDayDate === dateKey;
          },
        );

        if (existingSessionDay) {
          existingId = existingSessionDay.id;
        }
      }

      if (storedSessionDay) {
        startTime = storedSessionDay.startTime;
        endTime = storedSessionDay.endTime;
      } else if (initialData.sessionDays === undefined) {
        startTime = SESSION_DAY_DEFAULT_TIME.start;
        endTime = SESSION_DAY_DEFAULT_TIME.end;
      } else {
        const existingSessionDay = initialData.sessionDays.find(
          (sessionDay) => {
            const sessionDayDate = sessionDay.startDate
              ? format(parseISO(sessionDay.startDate), "yyyy-MM-dd")
              : "";
            return sessionDayDate === dateKey;
          },
        );

        if (existingSessionDay) {
          const displayProps =
            getDisplayPropertiesForSessionDay(existingSessionDay);
          startTime = displayProps.startTime || SESSION_DAY_DEFAULT_TIME.start;
          endTime = displayProps.endTime || SESSION_DAY_DEFAULT_TIME.end;
        } else {
          startTime = SESSION_DAY_DEFAULT_TIME.start;
          endTime = SESSION_DAY_DEFAULT_TIME.end;
        }
      }

      const createIsoDateTime = (date: Date, time: string) => {
        const newDate = new Date(date);
        const [hours, minutes] = time.split(":").map(Number);
        newDate.setHours(hours, minutes, 0, 0);
        return newDate.toISOString();
      };

      return {
        ...(existingId !== undefined && { id: existingId }),
        startDate: createIsoDateTime(day, startTime),
        endDate: createIsoDateTime(day, endTime),
      };
    },
    [
      initialData.sessionDays,
      getDisplayPropertiesForSessionDay,
      memoizedStoredSessionDays,
    ],
  );

  const syncSessionDaysWithRedux = React.useCallback(
    (sessionDays: NonNullable<ReturnType<typeof createSessionDayEntry>>[]) => {
      if (isInitialMount.current) return;

      const timesToStore = sessionDays
        .map((sessionDay) => {
          const displayProps = getDisplayPropertiesForSessionDay(sessionDay);
          return {
            date: displayProps.date,
            startTime: displayProps.startTime,
            endTime: displayProps.endTime,
          };
        })
        .filter((time) => time.date);

      const newSessionDays = timesToStore.filter(
        (time) => !memoizedStoredSessionDays[time.date],
      );

      if (newSessionDays.length > 0) {
        dispatch(setSessionDayTimes({ formId, times: newSessionDays }));
      }
    },
    [
      getDisplayPropertiesForSessionDay,
      dispatch,
      formId,
      memoizedStoredSessionDays,
    ],
  );

  const handleDateChange =
    (fieldName: "startDate" | "endDate") => (selectedDate: Date | null) => {
      if (!selectedDate) {
        setValue(fieldName, "");
        return;
      }

      setValue(fieldName, selectedDate.toISOString());

      if (fieldName === "startDate" && !watch("endDate")) {
        setValue("endDate", selectedDate.toISOString());
      }
    };

  const parseIsoString = (value: string | undefined): Date | null => {
    if (!value) return null;
    try {
      return new Date(value);
    } catch {
      return null;
    }
  };

  const updateSessionDayTime = (
    index: number,
    timeType: "start" | "end",
    timeValue: string,
  ) => {
    const currentSessionDay = fields[index];
    if (!currentSessionDay) return;

    const displayProps = getDisplayPropertiesForSessionDay(currentSessionDay);
    if (!displayProps.date) return;

    const isStartTime = timeType === "start";
    const dateField = isStartTime ? "startDate" : "endDate";
    const otherTimeField = isStartTime ? "endTime" : "startTime";

    if (!selectedTimezone?.value) {
      const createIsoDateTime = (dateKey: string, time: string) => {
        const newDate = new Date(dateKey + "T00:00:00");
        const [hours, minutes] = time.split(":").map(Number);
        newDate.setHours(hours, minutes, 0, 0);
        return newDate.toISOString();
      };

      setValue(
        `sessionDays.${index}.${dateField}`,
        createIsoDateTime(displayProps.date, timeValue),
      );

      const otherTime = displayProps[otherTimeField];
      dispatch(
        setSessionDayTime({
          formId,
          date: displayProps.date,
          ...(isStartTime
            ? { startTime: timeValue, endTime: otherTime }
            : { startTime: otherTime, endTime: timeValue }),
        }),
      );
      return;
    }

    const finalDate = convertTimezoneToUtc(
      displayProps.date,
      timeValue,
      selectedTimezone.value,
    );

    setValue(`sessionDays.${index}.${dateField}`, finalDate.toISOString());

    const otherTime = displayProps[otherTimeField];

    dispatch(
      setSessionDayTime({
        formId,
        date: displayProps.date,
        ...(isStartTime
          ? { startTime: timeValue, endTime: otherTime }
          : { startTime: otherTime, endTime: timeValue }),
      }),
    );
  };

  // Remove a single meeting day from the range (the "trash" control). The day
  // simply stops being part of `sessionDays`; on save the backend's
  // delete-missing logic drops the corresponding EventSessionDay (and its
  // attendance cascades). Removal persists because the regeneration effect only
  // rebuilds the list when the start/end range changes.
  const removeSessionDay = React.useCallback(
    (index: number) => {
      remove(index);
    },
    [remove],
  );

  const handleSubmitCreate = React.useCallback(
    (values: EventSessionFormValues) => {
      if (!eventUuid) {
        console.error("eventUuid is required for form submission");
        return;
      }

      const sessionDays =
        values.sessionDays?.map(
          (sessionDay: { startDate: string; endDate: string }) => ({
            startDate: sessionDay.startDate,
            endDate: sessionDay.endDate,
          }),
        ) || [];

      const payload = {
        eventUuid,
        name: values.name,
        startDate: values.startDate,
        endDate: values.endDate,
        maxEnrollments: values.maxEnrollments,
        virtualLink: values.virtualLink || null,
        notes: values.notes || null,
        isPublished: values.isPublished,
        isVirtualOnly: values.isVirtualOnly,
        venueId: values.venueId?.id ?? null,
        timezoneId: values.timezoneId ?? null,
        instructorId: values.instructorId?.id ?? null,
        sessionDays,
      };

      dispatch(
        createEventSessionAction(payload, () => {
          clearFormSessionDays();
          showNotification(
            "Success",
            "Event session created successfully.",
            "success",
          );
          onSuccessCallback?.();
        }),
      );
    },
    [eventUuid, dispatch, showNotification, onSuccessCallback],
  );

  const handleSubmitUpdate = React.useCallback(
    (values: EventSessionFormValues) => {
      if (!eventUuid) {
        console.error("eventUuid is required for form submission");
        return;
      }

      if (!sessionUuid) {
        console.error("sessionUuid is required for edit mode");
        return;
      }

      const sessionDays =
        values.sessionDays?.map(
          (sessionDay: {
            id?: number;
            startDate: string;
            endDate: string;
          }) => ({
            ...(sessionDay.id !== undefined && { id: sessionDay.id }),
            startDate: sessionDay.startDate,
            endDate: sessionDay.endDate,
          }),
        ) || [];

      const payload = {
        eventUuid,
        name: values.name,
        startDate: values.startDate,
        endDate: values.endDate,
        maxEnrollments: values.maxEnrollments,
        virtualLink: values.virtualLink || null,
        notes: values.notes || null,
        isPublished: values.isPublished,
        isVirtualOnly: values.isVirtualOnly,
        venueId: values.venueId?.id ?? null,
        timezoneId: values.timezoneId ?? null,
        instructorId: values.instructorId?.id ?? null,
        sessionDays,
      };

      dispatch(
        updateEventSessionAction(sessionUuid, payload, () => {
          showNotification(
            "Success",
            "Event session updated successfully.",
            "success",
          );
          onSuccessCallback?.();
        }),
      );
    },
    [eventUuid, sessionUuid, dispatch, showNotification, onSuccessCallback],
  );

  const submitHandler = isEditMode ? handleSubmitUpdate : handleSubmitCreate;

  const handleFormSubmit = (data: EventSessionFormValues) => {
    submitHandler(data);
  };

  const clearFormSessionDays = React.useCallback(() => {
    dispatch(clearSessionDays({ formId }));
  }, [dispatch, formId]);

  const handleClose = React.useCallback(() => {
    clearFormSessionDays();
  }, [clearFormSessionDays]);

  React.useEffect(() => {
    setIsTimezonesLoading(true);
    fetchEventSessionMetadata()
      .then((res) => {
        const tzData = res.data.timezones.map((tz) => ({
          label: tz.name,
          value: tz.identifier,
          id: tz.id,
        }));
        setTimezones(tzData);
      })
      .finally(() => setIsTimezonesLoading(false));
  }, []);

  React.useEffect(() => {
    if (!hasProcessedInitialData.current && initialData.sessionDays?.length) {
      hasProcessedInitialData.current = true;

      const timesToStore = initialData.sessionDays
        .map((sessionDay) => {
          const displayProps = getDisplayPropertiesForSessionDay(sessionDay);
          return {
            date: displayProps.date,
            startTime: displayProps.startTime,
            endTime: displayProps.endTime,
          };
        })
        .filter((time) => time.date);

      if (timesToStore.length > 0) {
        dispatch(setSessionDayTimes({ formId, times: timesToStore }));
      }
    }
  }, [
    initialData.sessionDays,
    dispatch,
    formId,
    getDisplayPropertiesForSessionDay,
  ]);

  React.useEffect(() => {
    if (
      selectedTimezone?.value &&
      fields.length > 0 &&
      initialData.sessionDays === undefined
    ) {
      fields.forEach((field, index) => {
        const displayProps = getDisplayPropertiesForSessionDay(field);
        if (
          displayProps.date &&
          displayProps.startTime &&
          displayProps.endTime
        ) {
          const startDateUtc = convertTimezoneToUtc(
            displayProps.date,
            displayProps.startTime,
            selectedTimezone.value,
          );
          const endDateUtc = convertTimezoneToUtc(
            displayProps.date,
            displayProps.endTime,
            selectedTimezone.value,
          );

          setValue(
            `sessionDays.${index}.startDate`,
            startDateUtc.toISOString(),
          );
          setValue(`sessionDays.${index}.endDate`, endDateUtc.toISOString());
        }
      });
    }
  }, [
    selectedTimezone?.value,
    fields,
    initialData.sessionDays,
    getDisplayPropertiesForSessionDay,
    setValue,
  ]);

  React.useEffect(() => {
    const currentStart = formValues.startDate;
    const currentEnd = formValues.endDate;

    // First run while editing a session that already has saved days: adopt the
    // saved (possibly date-trimmed) list as-is instead of regenerating the full
    // interval — regenerating would re-add dates the admin removed. Record the
    // range so later re-renders don't trigger a regen and resurrect them.
    if (
      lastGeneratedRangeRef.current === null &&
      initialData.sessionDays !== undefined
    ) {
      lastGeneratedRangeRef.current = { start: currentStart, end: currentEnd };
      if (isInitialMount.current) {
        isInitialMount.current = false;
      }
      return;
    }

    // Only regenerate when the date range actually changes. Identity-only churn
    // in the deps (callbacks recreated when redux times update, or after a
    // manual removal) must not rebuild the list, or exclusions would be lost.
    if (
      lastGeneratedRangeRef.current !== null &&
      lastGeneratedRangeRef.current.start === currentStart &&
      lastGeneratedRangeRef.current.end === currentEnd
    ) {
      return;
    }
    lastGeneratedRangeRef.current = { start: currentStart, end: currentEnd };

    if (!currentStart || !currentEnd) {
      replace([]);
      return;
    }

    try {
      const startDate = parseISO(currentStart);
      const endDate = parseISO(currentEnd);
      const days = eachDayOfInterval({ start: startDate, end: endDate });
      const sessionDays = days
        .map(createSessionDayEntry)
        .filter(Boolean) as NonNullable<
        ReturnType<typeof createSessionDayEntry>
      >[];

      replace(sessionDays);
      syncSessionDaysWithRedux(sessionDays);
    } catch (error) {
      console.error("Error parsing dates:", error);
      replace([]);
    }

    if (isInitialMount.current) {
      isInitialMount.current = false;
    }
  }, [
    formValues.startDate,
    formValues.endDate,
    replace,
    createSessionDayEntry,
    syncSessionDaysWithRedux,
    initialData.sessionDays,
  ]);

  return {
    formMethods,
    handleSubmit,
    control,
    isSubmitting,
    fields,
    timezones,
    isTimezonesLoading,
    maxEnrollmentsValue: formValues.maxEnrollments,
    isVirtualOnlyValue: formValues.isVirtualOnly,
    selectedTimezone,
    timeOptions,
    handleDateChange,
    parseIsoString,
    updateSessionDayTime,
    removeSessionDay,
    handleFormSubmit,
    getDisplayPropertiesForSessionDay,
    clearFormSessionDays,
    handleClose,
    formId,
    initialData,
    isLoading,
  };
}
